import { DbRecentResult } from '@apm/contracts';
import { useAlertRules } from '../alerts/useAlerts';
import { useAccess } from '../auth/access';
import { usePolling } from '../hooks/usePolling';
import { useQuery } from '../hooks/useQuery';
import type { StreamStatus } from '../store/stream';

/**
 * DB 감시·알림 감시를 다시 읽는 주기 (44회차). 43회차까지는 화면을 열 때 한 번만 읽어,
 * 그 뒤 DB 감시가 끊겨도 실시간 화면 맨 위에 "연결됨"이 남았다
 */
const REFRESH_MS = 30_000;

/**
 * 관제 연결 상태 한 줄 (19회차 보완, 경주 비교 "가져올 것").
 *
 * 실시간 화면 맨 위에서 관제 서버 쪽 세 갈래가 살아 있는지 한 줄로 본다.
 *   - 실시간 데이터: 이 화면이 관제 서버와 맺은 실시간 연결
 *   - DB 감시: 관제 서버가 대상 DB에 붙어 있는지 (`GET /api/db/recent`)
 *   - 알림 감시: 규칙 판정 엔진이 도는지 (`GET /api/alerts/rules`)
 *
 * 화면 첫 줄이라 옛 밀도에 맞춰 작은 글자 한 줄로 둔다. 카드로 키우지 않는다.
 */

type Level = 'ok' | 'off' | 'bad' | 'pending';

const LEVEL_DOT: Record<Level, string> = {
  ok: 'bg-ok',
  off: 'bg-muted',
  bad: 'bg-error',
  pending: 'bg-fail',
};

const LEVEL_TEXT: Record<Level, string> = {
  ok: 'text-ok',
  off: 'text-muted',
  bad: 'text-error',
  pending: 'text-fail',
};

const STREAM_LEVEL: Record<StreamStatus, Level> = {
  idle: 'off',
  connecting: 'pending',
  open: 'ok',
  error: 'bad',
};

const STREAM_TEXT: Record<StreamStatus, string> = {
  idle: '끊김',
  connecting: '연결 중',
  open: '연결됨',
  error: '끊김, 다시 연결 중',
};

export function ConnectionStatus({ stream }: { stream: StreamStatus }) {
  const version = usePolling(REFRESH_MS);
  // DB 감시·알림 규칙은 관리자만 보는 자리다 (52회차). 일반 계정은 부르지도 그리지도 않는다
  const { admin } = useAccess();
  const db = useQuery(admin ? '/api/db/recent?minutes=1' : undefined, DbRecentResult, version);
  const rules = useAlertRules(version, admin);

  const dbStats = db.data?.stats;
  const dbItem: { level: Level; text: string } =
    dbStats === undefined
      ? db.error !== undefined
        ? { level: 'bad', text: '확인 실패' }
        : { level: 'pending', text: '확인 중' }
      : !dbStats.enabled
        ? { level: 'off', text: '꺼짐' }
        : dbStats.connected
          ? { level: 'ok', text: '연결됨' }
          : { level: 'bad', text: '끊김' };

  const engine = rules.data?.stats;
  const alertItem: { level: Level; text: string } =
    engine === undefined
      ? rules.error !== undefined
        ? { level: 'bad', text: '확인 실패' }
        : { level: 'pending', text: '확인 중' }
      : engine.enabled
        ? { level: 'ok', text: '동작 중' }
        : { level: 'off', text: '꺼짐' };

  return (
    <p className="tabular flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
      <span className="font-medium text-ink">관제 연결 상태</span>
      <Item label="실시간 데이터" level={STREAM_LEVEL[stream]} text={STREAM_TEXT[stream]} />
      {admin ? (
        <>
          <Item label="DB 감시" level={dbItem.level} text={dbItem.text} />
          <Item label="알림 감시" level={alertItem.level} text={alertItem.text} />
        </>
      ) : null}
    </p>
  );
}

function Item({ label, level, text }: { label: string; level: Level; text: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <i aria-hidden="true" className={`size-2 rounded-full ${LEVEL_DOT[level]}`} />
      {label} <span className={LEVEL_TEXT[level]}>{text}</span>
    </span>
  );
}
